import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import ArrowIcon from 'assets/icons/arrow.svg?react';
import EditIcon from 'assets/icons/edit.svg?react';
import RefreshIcon from 'assets/icons/refresh_24.svg?react';
import UnlockIcon from 'assets/icons/unlock.svg?react';
import Eye from 'assets/icons/read-only-eye.svg?react';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Links } from 'application/constants/links';
import { AppLink } from 'components/uikit/buttons/AppLink';
import { QueryFactory } from 'services/api';

import styles from './Header.module.scss';
import { useModal } from 'components/uikit/modal/useModal';
import { TreeItems } from 'dnd-kit-sortable-tree';
import { useQueryClient } from '@tanstack/react-query';
import animationData from 'assets/animations/circle-loader.json';
import { Lottie } from 'components/animations/Lottie';
import {
  DocumentEditingType,
  ExternalEditingType,
  OperationType,
  ProjectDto,
  ProjectElement,
  ProjectResourceType,
  SystemVariableType,
} from 'services/api/api-client.types';
import { Emoji } from 'emoji-picker-react';
import { constantSessionId } from 'helpers/sessionProvider';
import { Trans } from 'react-i18next';
import { editTreeItem } from 'helpers/documents/documentsObserver';
import { BaseNode } from 'helpers/documents/types/base';
import i18next from 'i18next';
import { DropDownInput } from '../uikit/inputs/dropdown/DropDownInput.tsx';
import { Input, Props } from '../uikit/inputs/Input.tsx';
import { FeatureFlags } from 'application/constants/env-variables.ts';
import { useDocumentContext } from 'pages/authorized/documents/documentContext/DocumentContext.tsx';
import { BrightTooltip } from '../tooltips/BrightTooltip.tsx';
import { AppMenu } from '../uikit/menu/AppMenu.tsx';
import Dots from 'assets/icons/dots.svg?react';
import { ProjectModal } from 'pages/authorized/projects/components/projectModal/ProjectModal.tsx';
import { DeleteProjectModal } from 'pages/authorized/projects/components/deleteProjectModal/DeleteProjectModal.tsx';
import { useNavigate } from 'react-router';
import { UserCircle } from './UserCircle/UserCircle.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast';
import { PermissionGate } from 'helpers/projectPermissions/PermissionGate.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { ProjectProvider } from 'helpers/projectPermissions/ProjectContext.tsx';
import { PermissionTextButton } from '../../helpers/projectPermissions/components/PermissionTextButton.tsx';
import { getVariableLockInfo } from 'pages/authorized/variables/components/variableCard/useVariableCardAppMenuItems.tsx';
import { useVariableLockSync } from 'pages/authorized/variables/useVariableLockSync.ts';

export const Header: React.FC = () => {
  const i18n = useScopedTranslation('Component.Header');
  const buttonTranslation = useScopedTranslation('Button.Title');
  const unlockModalTranslation = useScopedTranslation(
    'Component.UnlockDocumentModal',
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const projectsMatch = Links.Authorized.Projects.useMatch();
  const dashboardMatch = Links.Authorized.Dashboard.useMatch();
  const projectDetailsMatch = Links.Authorized.ProjectDetails.useMatch();
  const createDocumentDetailsMatch = Links.Authorized.CreateDocument.useMatch();
  const documentsDetailsMatch = Links.Authorized.OpenDocument.useMatch();

  const projectId =
    projectDetailsMatch?.params.projectId ??
    documentsDetailsMatch?.params.projectId ??
    createDocumentDetailsMatch?.params.projectId;
  const projectQuery = QueryFactory.ProjectQuery.useGetQuery(projectId!, {
    enabled: !!projectId,
    throwOnError: false,
  });

  const templateDetailsMatch = Links.Authorized.OpenTemplate.useMatch();
  const templatesMatch = Links.Authorized.Templates.useMatch();
  const templatesMergingMatch = Links.Authorized.TemplatesMerging.useMatch();
  const revisionId = documentsDetailsMatch?.params.releaseId;
  const documentId = documentsDetailsMatch?.params.documentId;

  const { documentState, setDocumentState, documentQuery, editingUserQuery } =
    useDocumentContext();

  const templateId = templateDetailsMatch?.params.templateId;
  const templateQuery = QueryFactory.TemplateQuery.useGetQuery(templateId!, {
    enabled: templateId !== undefined,
  });

  const office365LinkQuery =
    QueryFactory.ExternalEditingQuery.useGetEditingDocumentLinkQuery(
      documentId!,
      ExternalEditingType.SharepointWordBrowser,
      {
        enabled:
          !!documentId &&
          editingUserQuery.data?.editingType === DocumentEditingType.Office365,
      },
    );
  const wordLinkQuery =
    QueryFactory.ExternalEditingQuery.useGetEditingDocumentLinkQuery(
      documentId!,
      ExternalEditingType.SharepointWordApp,
      {
        enabled:
          !!documentId &&
          editingUserQuery.data?.editingType === DocumentEditingType.Office365,
      },
    );

  const [documentName, setDocumentName] = useState(documentQuery.data?.name);
  const [documentLoading, setDocumentLoading] = useState(false);
  const savedProjectId = useRef<number | null>(null);
  const savedProjectQuery = QueryFactory.ProjectQuery.useGetQuery(
    savedProjectId.current!,
    { enabled: !!savedProjectId.current },
  );

  const modals = useModal();

  useEffect(() => {
    setDocumentName(documentQuery.data?.name);
  }, [documentQuery.data]);

  // True if document is not locked or locked during current session
  const isAbleToEditDocument =
    editingUserQuery.data &&
    (!editingUserQuery.data.isLocked ||
      editingUserQuery.data.sessionIdentifier === constantSessionId) &&
    editingUserQuery.data.editingType !== DocumentEditingType.Office365;

  const renderJoinEditingInput = (props: Props) => (
    <PermissionGate
      permissions={[
        permission(ProjectResourceType.Document, OperationType.Update),
      ]}
    >
      <div
        className={styles.joinEditingInputContainer}
        onMouseDown={props.onMouseDown}
      >
        <EditIcon />
        <Input {...props} value={i18n.t('JoinEditing.text')} />
      </div>
    </PermissionGate>
  );

  const ms365Option = i18n.t('JoinEditing.office_365');
  const wordOption = i18n.t('JoinEditing.word');

  const menuAnchorRef = useRef<HTMLElement>(null);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [isEditProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [isDeleteProjectModalOpen, setDeleteProjectModalOpen] = useState(false);

  const onProjectEdit = () => {
    if (!projectId) return;
    setEditProjectModalOpen(true);
  };

  const onProjectDelete = () => {
    if (!projectId) return;
    setDeleteProjectModalOpen(true);
  };

  const projectMenuItems = useMemo(() => {
    return [
      {
        key: 'edit',
        text: i18n.t('edit_project'),
        className: styles.edit,
        onClick: () => {
          setShowProjectMenu(false);
          onProjectEdit();
        },
      },
      {
        key: 'delete',
        text: i18n.t('delete_project'),
        className: styles.delete,
        onClick: () => {
          setShowProjectMenu(false);
          onProjectDelete();
        },
      },
    ];
  }, [i18n]);

  return (
    <header className={styles.header}>
      <UserCircle />
      {(projectsMatch || dashboardMatch || projectQuery.data) && (
        <div className={styles.right}>
          {FeatureFlags.templateMergingPageEnabled() && (
            <AppLink
              className={styles.link}
              to={Links.Authorized.TemplatesMerging.link()}
            >
              <TextButton
                testId="to-templates-merging"
                className={styles.button}
                title={'Template Merging'}
                color={TextButtonColor.Primary}
              />
            </AppLink>
          )}

          <AppLink
            className={styles.link}
            to={Links.Authorized.Templates.link()}
          >
            <TextButton
              testId="to-templates"
              className={styles.button}
              title={i18n.t('templates')}
              color={TextButtonColor.Primary}
              onClick={() => {
                if (!projectDetailsMatch) return;
                const projectId = projectDetailsMatch.params.projectId;
                if (projectId) savedProjectId.current = projectId;
              }}
            />
          </AppLink>
        </div>
      )}
      {projectQuery.data && (
        <ProjectProvider data={projectQuery.data}>
          {projectDetailsMatch && (
            <nav className={styles.navContainer}>
              <AppLink
                className={styles.link}
                to={Links.Authorized.Projects.link()}
              >
                <TextButton
                  testId="back-to-projects"
                  className={styles.button}
                  title={i18n.t('projects')}
                  color={TextButtonColor.Primary}
                  icon={<ArrowIcon />}
                />
              </AppLink>
              <div className={styles.project}>
                <div className={styles.icon}>
                  <Emoji unified={projectQuery.data.iconCode} size={20} />
                </div>
                <div>{projectQuery.data?.name}</div>
              </div>
              {projectId && (
                <PermissionGate
                  permissions={[
                    permission(
                      ProjectResourceType.Project,
                      OperationType.Read,
                      OperationType.Delete,
                      OperationType.Update,
                    ),
                  ]}
                  allowForAdmin
                  ignoreFeatureFlag
                >
                  <div>
                    <div
                      ref={menuAnchorRef as any}
                      className={styles.dotMenu}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowProjectMenu(true);
                      }}
                    >
                      <Dots className={styles.dotMenuImage} />
                    </div>
                    <AppMenu
                      anchorEl={menuAnchorRef.current}
                      open={showProjectMenu}
                      onClose={() => {
                        setShowProjectMenu(false);
                      }}
                      menuItems={projectMenuItems}
                      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    />
                  </div>
                </PermissionGate>
              )}
            </nav>
          )}
          {(documentQuery.data || createDocumentDetailsMatch) && (
            <div className={styles.navContainer}>
              <AppLink
                className={styles.link}
                to={Links.Authorized.ProjectDetails.link(
                  {
                    projectId: projectId!,
                  },
                  { releaseId: revisionId },
                )}
              >
                <TextButton
                  testId="back-to-revision"
                  className={styles.button}
                  title={projectQuery?.data?.name}
                  color={TextButtonColor.Primary}
                  icon={<ArrowIcon />}
                />
              </AppLink>
              {documentLoading && (
                <Lottie
                  className={styles.nameLoader}
                  animationData={animationData}
                />
              )}
              <div className={styles.documentNameContainer}>
                {!documentLoading && (
                  <div title="DocumentName" className={styles.name}>
                    {documentQuery.data
                      ? documentName
                      : i18n.t('new_document_name')}
                  </div>
                )}
                {documentQuery.data && isAbleToEditDocument && (
                  <EditDocumentButton
                    documentId={documentId!}
                    initialDocumentId={documentQuery.data.initialId!}
                    projectId={projectId!}
                    revisionId={revisionId!}
                    documentName={documentName!}
                    setDocumentLoading={setDocumentLoading}
                  />
                )}
                {editingUserQuery.data &&
                  !isAbleToEditDocument &&
                  office365LinkQuery.data !== null &&
                  wordLinkQuery.data !== null && (
                    <div className={styles.readOnly}>
                      <Eye />
                      <Trans
                        i18nKey={'Component.Header.document_is_readonly'}
                        values={{
                          name: editingUserQuery.data.userName,
                          type: editingUserQuery.data.editingType,
                        }}
                        components={{
                          bold: <strong style={{ whiteSpace: 'pre-wrap' }} />,
                        }}
                      />
                      <PermissionTextButton
                        icon={<UnlockIcon />}
                        className={styles.unlockButton}
                        onClick={async () => {
                          const result = await modals.showConfirm({
                            title: unlockModalTranslation.t('title'),
                            text: unlockModalTranslation.t('text', {
                              user: editingUserQuery.data.userName,
                            }),
                            okButtonText: buttonTranslation.t('unlock'),
                          });
                          if (result) {
                            try {
                              await QueryFactory.EditingLockClient.relock(
                                documentId!,
                                true,
                              );
                            } catch (e) {
                              showErrorToast(e);
                            }
                            await queryClient.invalidateQueries({
                              queryKey:
                                QueryFactory.EditingLockQuery.checkLockQueryKey(
                                  documentId!,
                                ),
                            });
                          }
                        }}
                        permissions={[
                          permission(
                            ProjectResourceType.Document,
                            OperationType.Update,
                          ),
                        ]}
                      />
                      {editingUserQuery.data.editingType ===
                        DocumentEditingType.Office365 && (
                        <DropDownInput
                          className={styles.joinEditingSelector}
                          options={[ms365Option, wordOption]}
                          value={''}
                          onValueChanged={(val) => {
                            switch (val) {
                              case ms365Option:
                                window.open(office365LinkQuery.data, '_blank');
                                break;
                              case wordOption:
                                window.open(wordLinkQuery.data, '_blank');
                                break;
                            }
                          }}
                          classes={{ option: styles.joinEditingOption }}
                          endAdornmentClassname={styles.expandIcon}
                          renderInput={renderJoinEditingInput as any}
                          required={true}
                        />
                      )}
                      <BrightTooltip
                        title={i18next.t('Component.Header.SyncButton.tooltip')}
                      >
                        <TextButton
                          disabled={!documentState.hasChanges}
                          color={TextButtonColor.Primary}
                          onClick={() => {
                            setDocumentState((prevState) => ({
                              ...prevState,
                              enableDocumentUpdate: true,
                              hasChanges: false,
                            }));
                          }}
                          icon={<RefreshIcon />}
                          className={styles.refreshButton}
                        />
                      </BrightTooltip>
                    </div>
                  )}
              </div>
            </div>
          )}
        </ProjectProvider>
      )}
      {(templatesMatch || templateDetailsMatch || templatesMergingMatch) && (
        <nav className={styles.navContainer}>
          <AppLink
            className={styles.link}
            to={getTemplateBackLink(
              !!templateDetailsMatch,
              savedProjectId.current,
            )}
          >
            <TextButton
              testId="back-from-templates"
              title={getTemplateBackTitle(
                !!templateDetailsMatch,
                savedProjectQuery.data,
              )}
              className={styles.button}
              color={TextButtonColor.Primary}
              icon={<ArrowIcon />}
              onClick={() => (savedProjectId.current = null)}
            />
          </AppLink>
          {templateQuery.data && (
            <div title="TemplateName" className={styles.name}>
              {templateQuery.data.name}
            </div>
          )}
        </nav>
      )}
      <ProjectModal
        id={projectId}
        isOpen={isEditProjectModalOpen}
        onClose={() => {
          setEditProjectModalOpen(false);
        }}
      />
      {projectQuery.data && (
        <DeleteProjectModal
          onClose={() => {
            setDeleteProjectModalOpen(false);
          }}
          afterDelete={() => {
            navigate(Links.Authorized.Projects.link());
          }}
          projectId={projectQuery.data.id}
          projectName={projectQuery.data.name}
          isOpen={isDeleteProjectModalOpen}
        />
      )}
    </header>
  );
};

const EditDocumentButton: React.FC<{
  documentId: number;
  initialDocumentId: number;
  projectId: number;
  revisionId: number;
  documentName: string;
  setDocumentLoading: (loading: boolean) => void;
}> = ({
  documentId,
  initialDocumentId,
  projectId,
  revisionId,
  documentName,
  setDocumentLoading,
}) => {
  const modals = useModal();
  const queryClient = useQueryClient();

  const i18n = useScopedTranslation('Component.Header');
  const modalTranslation = useScopedTranslation('Component.CommonModals');
  const buttonTranslation = useScopedTranslation('Button.Title');

  const variablesQuery = QueryFactory.VariableQuery.useSearchQuery(
    {
      projectId,
      revisionId,
    },
    {
      enabled: revisionId !== undefined,
    },
  );

  const { applyVariablesRef, documentQuery, setDocumentState } =
    useDocumentContext();

  const queryData = documentQuery.data!;
  const documentNameVariable = variablesQuery.data?.data.find(
    (x) =>
      x.systemVariableType === SystemVariableType.Name &&
      x.basedOnDocument?.id === initialDocumentId,
  );

  useVariableLockSync(
    documentNameVariable ? [documentNameVariable.id] : [],
    revisionId,
    variablesQuery.refetch,
  );

  const lockInfo = getVariableLockInfo(
    documentNameVariable?.lockStatuses,
    documentId,
  );

  return (
    <PermissionTextButton
      icon={<EditIcon />}
      className={styles.editButton}
      tooltip={i18n.t('document_name_is_locked')}
      disabled={lockInfo.reason !== null}
      tooltipProps={{
        disableHoverListener: lockInfo.reason === null,
      }}
      onClick={async () => {
        const result = await modals.showPrompt({
          title: i18n.t('edit_document'),
          text: '',
          defaultValue: documentName!,
          fieldName: i18n.t('document_name_field'),
          okButtonText: buttonTranslation.t('save'),
          cancelButtonText: buttonTranslation.t('cancel'),
        });

        if (result) {
          const isDocumentExported = queryData.isExported;
          let confirmed = false;
          if (isDocumentExported) {
            confirmed = await modals.showConfirm({
              title: modalTranslation.t('confirm_change'),
              text: modalTranslation.t('confirm_change_message'),
            });
          }

          if (confirmed || !isDocumentExported) {
            try {
              setDocumentLoading(true);
              const doc = await renameDocument(
                revisionId!,
                documentId!,
                queryData.initialId!.toString(),
                result,
              );
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.VariableQuery.searchQueryKey(),
              });
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.DocumentQuery.getQueryKey(+documentId!),
              });

              setDocumentState((prevState) => ({
                ...prevState,
                name: result,
              }));

              applyVariablesRef.current?.(
                doc.variables
                  .filter(
                    (v) => v.systemVariableType === SystemVariableType.Name,
                  )
                  .map((v) => v.id),
              );
            } catch (e) {
              showErrorToast(e);
            } finally {
              setDocumentLoading(false);
            }
          }
        }
      }}
      permissions={[
        permission(ProjectResourceType.Document, OperationType.Update),
      ]}
    />
  );
};

async function renameDocument(
  revisionId: number,
  documentId: number,
  itemId: string,
  name: string,
) {
  const folders = (await QueryFactory.RevisionClient.get(revisionId)).folders;
  const parsedFolders = folders as TreeItems<BaseNode>;

  editTreeItem(parsedFolders, itemId, name, 'name');

  const doc = await QueryFactory.DocumentClient.patch(documentId!, {
    name: name,
  });
  await QueryFactory.RevisionClient.patch(revisionId, {
    folders: parsedFolders as ProjectElement[],
  });
  return doc;
}

function getTemplateBackTitle(
  isTemplateDetails: boolean,
  project?: ProjectDto,
): string {
  if (isTemplateDetails) return i18next.t('Component.Header.templates');

  return project ? project.name : i18next.t('Component.Header.projects');
}

function getTemplateBackLink(
  isTemplateDetails: boolean,
  savedProjectId: number | null,
) {
  if (isTemplateDetails) return Links.Authorized.Templates.link();

  return savedProjectId == null
    ? Links.Authorized.Projects.link()
    : Links.Authorized.ProjectDetails.link({
        projectId: savedProjectId,
      });
}
